// Iter160e (Randy pt-ytre-p1) — session-only LLM surface picker.
//
// The visitor / creator can override the LLM model + max output tokens
// on a per-surface basis, but ONLY for the current tab-session. Refresh
// wipes it. New tab wipes it. Logout wipes it. sessionStorage is exactly
// the shape we want — page-navigation within the same tab preserves the
// pick (so bouncing between YTRE and Public Q&A on the same surface
// keeps the choice), while any hard-reload starts clean.
//
// Storage key shape: `mysilo_session_llm_{surface}`. Payload shape:
// `{ model: str, max_output_tokens?: int }`. Both fields optional; an
// empty/missing model means "no override" (cascade resolves normally).
//
// We deliberately DO NOT scope per-silo — Randy's spec is "session-only
// for the surface". So if a user opens two silos in the same tab (via
// nav) they share the pick. New tab = fresh cascade.

const KEY = (surface) => `mysilo_session_llm_${surface}`;

export function readSessionLlmOverride(surface) {
  if (!surface || typeof sessionStorage === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(KEY(surface));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    const model = (parsed.model || "").trim();
    if (!model) return null;
    const out = { model };
    if (parsed.max_output_tokens && Number.isFinite(parsed.max_output_tokens)) {
      out.max_output_tokens = parsed.max_output_tokens;
    }
    return out;
  } catch {
    return null;
  }
}

export function writeSessionLlmOverride(surface, payload) {
  if (!surface || typeof sessionStorage === "undefined") return;
  try {
    if (!payload || !(payload.model || "").trim()) {
      sessionStorage.removeItem(KEY(surface));
      return;
    }
    const clean = { model: payload.model.trim() };
    if (payload.max_output_tokens) clean.max_output_tokens = Number(payload.max_output_tokens);
    sessionStorage.setItem(KEY(surface), JSON.stringify(clean));
  } catch {
    /* quota / disabled — silent */
  }
}

export function clearSessionLlmOverride(surface) {
  if (!surface || typeof sessionStorage === "undefined") return;
  try { sessionStorage.removeItem(KEY(surface)); } catch { /* */ }
}

// Turn an override into the query-body pair used across the /ask +
// /ytre/ask-silo endpoints. Returns an empty object when no override
// exists so callers can spread it unconditionally.
export function overrideAsAskBody(surface) {
  const ov = readSessionLlmOverride(surface);
  if (!ov) return {};
  const body = { model_override: ov.model };
  if (ov.max_output_tokens) body.max_output_tokens_override = ov.max_output_tokens;
  return body;
}

// Terse label for the pill button — "gemini-3.5-flash-lite" → "Gem 3.5 Lt".
// Matches the same shorthand pattern used in UsageOverview so users see
// a consistent nickname.
export function labelForModel(model) {
  const m = (model || "").toLowerCase();
  if (!m) return "";
  if (m.startsWith("gemini-3-pro")) return "Gem 3 Pro";
  if (m.startsWith("gemini-3-flash")) return "Gem 3 Fl";
  if (m.startsWith("gemini-2.5-pro")) return "Gem 2.5 Pro";
  if (m.startsWith("gemini-3.5-flash-lite")) return "Gem 3.5 Lt";
  if (m.startsWith("gemini-3.5-flash")) return "Gem 3.5 Fl";
  // Kept for historical rows: 2.5 Flash was deprecated Dec 1 2026.
  if (m.startsWith("gemini-2.5-flash")) return "Gem 2.5 Fl";
  if (m.startsWith("gpt-5")) return "GPT-5";
  if (m.startsWith("gpt-4")) return "GPT-4";
  if (m.startsWith("claude-opus")) return "Opus";
  if (m.startsWith("claude-sonnet")) return "Sonnet";
  if (m.startsWith("claude-haiku")) return "Haiku";
  return m.slice(0, 12);
}
